class DeliveryMethod {
  constructor(container) {
    this.container = container;
    if (!this.container) {
      return;
    }
    this.wrapper = this.container.querySelector('.delivery-methods');
    if(!this.wrapper) return;
    this.shopLocale = Theme.settings.shop_locale;
    this.deliveryMethodNotAvailable = this.wrapper.querySelector('.delivery-method-options__are-not-available');
    this.deliveryMethodOptions = this.wrapper.querySelector('.delivery-method-options');
    this.moreDeliveryOptions = this.wrapper.querySelector('#more-delivery-options');
    this.pickupDelivery = this.wrapper.querySelector('.pickup-delivery');
    this.pickupDeliveryMethodsScript = document.querySelectorAll('script.pickup-delivery-methods-script');
    this.country_iso_code = Theme.settings.country_iso_code || 'DK';
    this.isProcessingDelivery = false
    this.isHandlingDeliveryEvent = false;

    if(!this.deliveryMethodOptions || !this.deliveryMethodNotAvailable || !this.moreDeliveryOptions ) return;

    this.selectedMethod = null;
    this.setupDeliveryAccordion();

    this.initialize();
  }
  
  initialize() {
    this.methods = this.container.querySelectorAll('.delivery-method-item');
    document.addEventListener('free-delivery:applied', (event) => {
        const freeDelivery = event?.detail?.freeDelivery || false;
        this.fetchDeliveryMethodsEligibility();
    });
    // Re-apply the relocated free-shipping discount to the method list whenever the checkout
    // re-renders (e.g. once the automatic discount is detected after the Storefront cart loads).
    document.addEventListener('checkout:updated', () => {
        // Recompute the selected method's NET price (full − discount) once the free-shipping
        // discount is known, so window.checkout.deliveryPrice is correct even before any click.
        this.initDefaultShippingCode();
        this.applyFreeShippingDiscount();
    });
    this.getPickupPointsDataFromMetaobjects();
    this.initDefaultShippingCode();
    this.applyFreeShippingDiscount();
    this.setupEventListeners();
  }

  // Shows each delivery method's price with the free-shipping discount applied:
  // max(0, fullPrice - fsShip). Fully covered -> reuse the .free-delivery style (struck price +
  // "Gratis"); partially covered -> show the discounted price with the original struck before it.
  // data-delivery-price is left untouched so the order total still uses the full shipping price.
  applyFreeShippingDiscount() {
    const isFreeDeliveryApplied = window.checkout?.isFreeDeliveryApplied || false;
    if (isFreeDeliveryApplied) return; // handled by fetchDeliveryMethodsEligibility
    const fsShip = parseFloat(window.checkout?.freeShippingDiscountAmount) || 0;
    const items = this.container.querySelectorAll('.delivery-method-item');
    items.forEach(item => {
      const methodPrice = item.querySelector('.method-price');
      if (!methodPrice) return;
      const priceEl = methodPrice.querySelector('[data-checkout-delivery-item]');
      if (!priceEl) return;
      const fullPrice = parseFloat(item.dataset.deliveryPrice) || 0;
      let original = methodPrice.querySelector('.fs-original');

      if (fsShip <= 0 || fullPrice <= 0) {
        methodPrice.classList.remove('free-delivery');
        if (original) original.remove();
        if (fullPrice > 0) priceEl.textContent = Theme.Shopify.formatMoney(fullPrice, Theme.money_format);
        return;
      }

      const discounted = Math.max(0, fullPrice - fsShip);
      if (discounted === 0) {
        methodPrice.classList.add('free-delivery');
        if (original) original.remove();
        priceEl.textContent = Theme.Shopify.formatMoney(fullPrice, Theme.money_format);
      } else {
        methodPrice.classList.remove('free-delivery');
        priceEl.textContent = Theme.Shopify.formatMoney(discounted, Theme.money_format);
        if (!original) {
          original = document.createElement('span');
          original.className = 'fs-original struck-out-price';
          methodPrice.insertBefore(original, priceEl);
        }
        original.textContent = Theme.Shopify.formatMoney(fullPrice, Theme.money_format);
      }
    });
  }
  
  setupEventListeners() {
    this.container.addEventListener('checkout:address-filled', async (event) => {
      this.cleanPreviousDeliveryMethods();
      await this.handleDeliveryMethodsEvent(event);
    });
    document.addEventListener('address:autocomplete-filled', async (event) => {
      this.cleanPreviousDeliveryMethods();
      await this.handleDeliveryMethodsEvent(event);
    });

    this.container.addEventListener('checkout:address-not-filled-correctly', () => {
      this.deliveryMethodNotAvailable.classList.remove('d-none');
      this.deliveryMethodOptions.classList.add('d-none');
    });
  }

  async handleDeliveryMethodsEvent(event) {
    if (this.isHandlingDeliveryEvent) {
      console.warn('Already handling delivery methods event, skipping duplicate call');
      return;
    }
  
    this.isHandlingDeliveryEvent = true;
    
    try {
      this.deliveryMethodOptions.classList.add('loading');
      if(this.shopLocale === 'da-DK' || this.shopLocale === 'da'){
        const pickupPointsData = window.checkout?.pickupPointsDeliveryMethods;
        if(pickupPointsData){
          if(pickupPointsData.length > 0){
            await this.pickupDeliveryMethodsEvent(pickupPointsData, event);
          }
        }
      }
      this.fetchDeliveryMethodsEligibility();
      this.deliveryMethodNotAvailable.classList.add('d-none');
      this.deliveryMethodOptions.classList.remove('d-none');
    } finally {
      this.isHandlingDeliveryEvent = false;
    }
  }

  async pickupDeliveryMethodsEvent(pickupPointsData, event) {
      if(this.isProcessingDelivery){ 
        console.warn("Processing delivery methods")
        return;
      }
      else {
          this.isProcessingDelivery = true;
          const fetchPromises = pickupPointsData.map(async (pickupPoint) => {
            const pickupPointLimt = pickupPoint?.pickup_point_limit || 3;
            return this.fetchDeliveryMethods(
              pickupPoint, 
              pickupPoint?.carrier_code, 
              this.country_iso_code, 
              event.detail.address, 
              event.detail.city, 
              event.detail.postcode, 
              pickupPointLimt
            );
          });
          await Promise.all(fetchPromises);
          this.isProcessingDelivery = false;
      }

  }
  
  setupDeliveryAccordion() {
    const trigger = this.wrapper.querySelector('#delivery-accordion-trigger');
    const triggerWrapper = trigger.closest('.delivery-method-item');
    if (trigger && this.moreDeliveryOptions && triggerWrapper) {
      const triggerArrow = triggerWrapper.querySelector('.accordion-trigger-arrow');
      triggerWrapper.addEventListener('click', () => {
        const isHidden = this.moreDeliveryOptions.style.display === 'none' || this.moreDeliveryOptions.style.display === '';
        this.moreDeliveryOptions.style.display = isHidden ? 'block' : 'none';
        trigger.textContent = isHidden ? Theme?.strings?.show_fewer_options : Theme?.strings?.show_other_delivery_options;
        if (triggerArrow) {
            triggerArrow.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)';
        }
      }); 
    }
  }

  async fetchDeliveryMethods(deliveryMetaobjectData, carrier_code, country_code, address, city, zipCode, pickupPointLimit) {
    try{
      const url = `https://wellvita-api.netlify.app/.netlify/functions/getPickupPoints?carrier_code=${carrier_code}&country_code=${country_code}&zipcode=${zipCode}&city=${city}&address=${address}&max_shipping_pickup_points=${pickupPointLimit}`;
      const response = await fetch(url);
      const responseBody = await response.json();
      if(!responseBody) throw new Error('No data received');
      if(responseBody.error) throw new Error(responseBody.error);

      const deliveryDataFromAPI = responseBody.data;
      if(deliveryDataFromAPI.length > 0){
        this.renderDeliveryMethods(deliveryMetaobjectData, deliveryDataFromAPI);
      }
    }
    catch(error){
      console.log("error", error);
    }
  }

  fetchDeliveryMethodsEligibility() {
    const isFreeDeliveryApplied = window.checkout?.isFreeDeliveryApplied || false;
    const deliveryMethodItems = this.container.querySelectorAll('.delivery-method-item');
    if(isFreeDeliveryApplied){
      deliveryMethodItems.forEach(item => {
        const itemPrice = item.querySelector('.method-price');
        if(!itemPrice) return;
        itemPrice.classList.add('free-delivery');
      });
    } else {
      deliveryMethodItems.forEach(item => {
        const itemPrice = item.querySelector('.method-price');
        if(!itemPrice) return;
        itemPrice.classList.remove('free-delivery');
      });
    }
    // Re-apply the free-shipping relocation: the loop above strips the free-delivery class
    // (used for the fully-covered case), so this must run last to keep those methods discounted.
    this.applyFreeShippingDiscount();
  }

  parsePickupScript() {
    if (!this.pickupDeliveryMethodsScript) return null;
    try {
      this.pickupDeliveryMethodsScript.forEach(script => {
        let scriptContent = script.textContent;
        scriptContent = scriptContent.replace(/\s+/g, '');
        const scriptContentFormatted =  scriptContent.startsWith("'") && scriptContent.endsWith("'") ? scriptContent.slice(1, -1) : scriptContent;
        if(window.checkout?.pickupPointsDeliveryMethods) window.checkout?.pickupPointsDeliveryMethods.push(JSON.parse(scriptContentFormatted));
      });
      return true;
    } catch (e) {
      console.error("Error parsing pickup script:", e);
      return null;
    }
  }

  createDeliveryMethodElement(item, template, deliveryMetaobjectData) {
    const templateContent = template.content.cloneNode(true);
    const deliveryMethodItem = templateContent.querySelector('.delivery-method-item');
    const distance = this.calculateDistance(item.distance);
    const pickupPrice = this.validateAndFormatPrice(deliveryMetaobjectData?.price);
    if (!pickupPrice) return null;

    const formattedMoney = Theme.Shopify.formatMoney(pickupPrice, Theme.money_format);
    const fullTitle = this.generateDeliveryTitle(item, distance);
    const fullSubtitle = this.generateDeliverySubtitle(item, deliveryMetaobjectData);

    this.setupDeliveryMethodElement(deliveryMethodItem, item, deliveryMetaobjectData, {
      price: pickupPrice,
      formattedMoney,
      fullTitle,
      fullSubtitle
    });

    return templateContent;
  }

  calculateDistance(distance) {
    if (distance === null || distance === undefined) return '';
    return Number.isInteger(distance) ? (distance / 1000).toFixed(1) : '';
  }

  validateAndFormatPrice(price) {
    if (!price) return null;
    const pickupPrice = Number(price) * 100;
    return (Number.isInteger(pickupPrice) && pickupPrice > 0) ? pickupPrice : null;
  }

  generateDeliveryTitle(item, distance) {
    let carrierCodeName = '';
    if(item?.carrier_code === 'pdk') carrierCodeName = 'PostNord - ';
    else carrierCodeName = `${item.carrier_code.toUpperCase()} - `;
    const distanceText = distance ? `(${distance} km)` : '';
    return `${carrierCodeName}${item?.name || ''} ${distanceText}`.trim();
  }

  generateDeliverySubtitle(item, pickupScriptContentJSON) {
    const address = [item?.address, item?.zipcode, item?.city]
      .filter(Boolean)
      .join(', ');
    const deliveryTime = pickupScriptContentJSON?.delivery_time || '';
    return deliveryTime ? `${address} - ${deliveryTime} ` : address;
  }

  setupDeliveryMethodElement(element, item, deliveryMetaobjectData, { price, formattedMoney, fullTitle, fullSubtitle }) {
    element.setAttribute('data-delivery-price', price);
    element.setAttribute('pickup-point', true);
    let shippingCode;
    if(this.shopLocale === 'da-DK' || this.shopLocale === 'da') { 
      const carrierCode = deliveryMetaobjectData?.carrier_code || '';
      shippingCode = `dk_${carrierCode}_service_point_${item.id}`;
    }
    else shippingCode = `${item?.id ? item.id : ''}`;   

    const input = element.querySelector('input');
    if(!input) return;
    input.value = `pickup${item.id}`;
    input.id = `pickup${item.id}`;
    input.setAttribute('data-shipping-code', shippingCode);
    input.setAttribute('data-delivery-price', price);
    input.setAttribute('data-full-title', fullTitle);
    
    const label = element.querySelector('label');
    label.setAttribute('for', `pickup${item.id}`);
    
    element.querySelector('.method-name').textContent = fullTitle;
    element.querySelector('.method-subtitle').textContent = fullSubtitle;
    element.querySelector('.method-price span[data-checkout-delivery-item]').textContent = formattedMoney;
  }

  renderDeliveryMethods(deliveryMetaobjectData, deliveryDataFromAPI) {
    const template = document.querySelector('#delivery-method-template');
    if (!template) return;

    deliveryDataFromAPI.forEach(item => {
      const deliveryMethodElement = this.createDeliveryMethodElement(item, template, deliveryMetaobjectData);
      if (deliveryMethodElement) {
        this.pickupDelivery.appendChild(deliveryMethodElement);
      }
    });


    this.onDeliveryMethodChange();
    this.applyFreeShippingDiscount();
    this.deliveryMethodOptions.classList.remove('loading');
  }

  getPickupPointsDataFromMetaobjects() {
    if(window.checkout?.pickupPointsDeliveryMethods.length > 0) return;
    const pickupScriptContentJSON = this.parsePickupScript();
    if (!pickupScriptContentJSON) return;
  }

  onDeliveryMethodChange() {
    const deliveryMethods = this.wrapper.querySelectorAll('input[name="delivery-method"]');

    if (deliveryMethods.length > 0) {
      this.initDefaultShippingCode(); 
        deliveryMethods.forEach(method => {
            method.addEventListener('change', function() {
              const deliveryPriceAttr = method.getAttribute('data-delivery-price');
              const deliveryPriceNum = deliveryPriceAttr ? parseFloat(deliveryPriceAttr) : 0;
              const fullPrice = isNaN(deliveryPriceNum) ? 0 : deliveryPriceNum;
              const fsShip = parseFloat(window.checkout?.freeShippingDiscountAmount) || 0;
              window.checkout.deliveryMethod = method.getAttribute('data-full-title');
              // Net shipping actually charged: full minus the relocated free-shipping discount (floored at 0).
              window.checkout.deliveryPrice = Math.max(0, fullPrice - fsShip);
              window.checkout.deliveryCode = method.getAttribute('data-shipping-code');
              document.dispatchEvent(new CustomEvent('checkout:updated', { detail: { fullTitle: method.getAttribute('data-full-title'), deliveryPrice: method.getAttribute('data-delivery-price') } }));
            });
        });
    }
  }

  initDefaultShippingCode() {
    const defaultShippingCode = this.wrapper.querySelector('input[name="delivery-method"]:checked');
    if (defaultShippingCode) {
      const deliveryPriceAttr = defaultShippingCode.getAttribute('data-delivery-price');
      const deliveryPriceNum = deliveryPriceAttr ? parseFloat(deliveryPriceAttr) : 0;
      const fullPrice = isNaN(deliveryPriceNum) ? 0 : deliveryPriceNum;
      const fsShip = parseFloat(window.checkout?.freeShippingDiscountAmount) || 0;
      window.checkout.deliveryMethod = defaultShippingCode.getAttribute('data-full-title');
      // Net shipping actually charged: full minus the relocated free-shipping discount (floored at 0).
      window.checkout.deliveryPrice = Math.max(0, fullPrice - fsShip);
      window.checkout.deliveryCode = defaultShippingCode.getAttribute('data-shipping-code');
    }
  }

  cleanPreviousDeliveryMethods() {
    this.pickupDelivery.innerHTML = '';
  }
}

export default DeliveryMethod;